import { Fragment, useCallback, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box,
  IconButton,
  Link,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { mergeOpsApiRef } from '../../api/MergeOpsApi';
import { E2eRunSummary, GroupE2eResults } from '../../api/types';
import { mergeOpsTokens } from '../../theme/tokens';
import {
  cellState,
  latestStartedAt,
  passedRollupText,
  runKind,
  runSummaryText,
} from './e2eSummary';

const useStyles = makeStyles(theme => {
  const t = mergeOpsTokens(theme);
  return {
    panel: {
      backgroundColor: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: t.radius,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1.5),
    },
    header: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1.25),
      flexWrap: 'wrap',
    },
    title: {
      fontWeight: 700,
    },
    envName: {
      fontWeight: 700,
    },
    meta: {
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
    },
    reload: {
      marginLeft: 'auto',
      alignSelf: 'center',
    },
    matrix: {
      overflowX: 'auto',
      marginTop: theme.spacing(1),
    },
    mgrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(96px, 160px) minmax(100px, 140px) 1fr',
      alignItems: 'center',
    },
    suiteHead: {
      fontSize: '0.66rem',
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: theme.palette.text.hint,
      paddingBottom: theme.spacing(0.5),
      textAlign: 'center',
    },
    rowName: {
      fontWeight: 600,
      fontSize: '0.82rem',
      padding: theme.spacing(0.5, 1, 0.5, 0),
      borderTop: `1px solid ${theme.palette.divider}`,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    mcell: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(0.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      height: '100%',
    },
    mpad: {
      borderTop: `1px solid ${theme.palette.divider}`,
      height: '100%',
    },
    cell: {
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      padding: theme.spacing(0, 1.25),
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      backgroundColor: t.badgeBg,
      border: `1px solid ${t.badgeBorder}`,
      color: theme.palette.text.secondary,
    },
    cellOk: {
      color: t.state.ready.fg,
      borderColor: t.state.ready.border,
      backgroundColor: t.state.ready.bg,
    },
    cellBad: {
      color: t.state.blocked.fg,
      borderColor: t.state.blocked.border,
      backgroundColor: t.state.blocked.bg,
    },
    cellNone: {
      color: theme.palette.text.hint,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
    // Failure detail: always rendered right under the row when the product's
    // suite has failures (no expand/collapse — discoverability beats space).
    detail: {
      gridColumn: '1 / -1',
      backgroundColor: t.isDark
        ? 'rgba(196, 196, 204, 0.05)'
        : 'rgba(15, 23, 42, 0.03)',
      border: `1px solid ${t.borderSoft}`,
      borderLeft: `3px solid ${t.state.blocked.border}`,
      borderRadius: t.radiusSm,
      margin: theme.spacing(0.25, 0, 1),
      padding: theme.spacing(1, 1.75),
    },
    detailTitle: {
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: theme.palette.text.hint,
      marginBottom: theme.spacing(0.5),
    },
    runLine: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      margin: theme.spacing(0.25, 0),
    },
    runBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      height: 21,
      padding: theme.spacing(0, 1),
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      backgroundColor: t.state.blocked.bg,
      border: `1px solid ${t.state.blocked.border}`,
      color: t.state.blocked.fg,
    },
    counts: {
      fontSize: '0.8rem',
    },
    when: {
      color: theme.palette.text.hint,
      fontSize: '0.76rem',
      fontVariantNumeric: 'tabular-nums',
    },
    links: {
      fontSize: '0.76rem',
      whiteSpace: 'nowrap',
    },
    allPass: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
      margin: theme.spacing(0.25, 0),
    },
    allPassMark: {
      color: t.state.ready.fg,
      fontWeight: 700,
    },
    failureList: {
      margin: theme.spacing(0.25, 0, 0.75),
      paddingLeft: theme.spacing(3.25),
      fontSize: '0.8rem',
    },
    failurePath: {
      color: theme.palette.text.secondary,
    },
    error: {
      color: theme.palette.text.secondary,
      fontSize: '0.82rem',
      marginTop: theme.spacing(0.5),
    },
  };
});

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
}

const RunLine = ({ run }: { run: E2eRunSummary }) => {
  const classes = useStyles();
  const actionsUrl = run.jobUrl ?? run.runUrl;
  return (
    <Box>
      <Box className={classes.runLine}>
        <span className={classes.runBadge}>
          {run.browserName}/{run.targetExecution}
        </span>
        <span className={classes.counts}>{runSummaryText(run)}</span>
        <span className={classes.when}>{formatTime(run.startedAt)}</span>
        <span className={classes.links}>
          {run.reportUrl && (
            <Link
              href={run.reportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Report
            </Link>
          )}
          {run.reportUrl && actionsUrl && ' · '}
          {actionsUrl && (
            <Link href={actionsUrl} target="_blank" rel="noopener noreferrer">
              GHA
            </Link>
          )}
        </span>
      </Box>
      {run.failedTestcases.length > 0 && (
        <ul className={classes.failureList}>
          {run.failedTestcases.map((tc, i) => (
            <li key={`${tc.filePath}:${tc.testcaseName}:${i}`}>
              <span className={classes.failurePath}>{tc.filePath}</span> ›{' '}
              {tc.reportDeepLink ? (
                <Link
                  href={tc.reportDeepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tc.testcaseName}
                </Link>
              ) : (
                tc.testcaseName
              )}
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

/** Failed runs individually, green runs rolled up into one line. */
const FailureDetail = ({
  product,
  runs,
}: {
  product: string;
  runs: E2eRunSummary[];
}) => {
  const classes = useStyles();
  const badRuns = runs.filter(run => runKind(run) === 'failed');
  const okRuns = runs.filter(run => runKind(run) !== 'failed');
  return (
    <Box className={classes.detail}>
      <Typography className={classes.detailTitle}>{product} · E2E</Typography>
      {badRuns.map(run => (
        <RunLine key={run.executionRunId} run={run} />
      ))}
      {okRuns.length > 0 && (
        <Box className={classes.allPass}>
          <span className={classes.allPassMark}>✓</span>
          {passedRollupText(okRuns, runs.length)}
          <span className={classes.when}>
            {formatTime(latestStartedAt(okRuns))}
          </span>
        </Box>
      )}
    </Box>
  );
};

/**
 * One env's results: dev and stg are separate worlds (a deploy decision looks
 * at exactly one of them), so each gets its own block instead of sharing a
 * table. Rows = every product of the group, columns = suites (E2E only until
 * AIT/SIT land in Phase 2).
 */
const EnvBlock = ({
  env,
  value,
  onReload,
  reloading,
}: {
  env: 'dev' | 'stg';
  value: GroupE2eResults;
  onReload: () => void;
  reloading: boolean;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.panel}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Test results</Typography>
        <Typography className={classes.envName}>· {env}</Typography>
        <Typography className={classes.meta}>
          from kwdp mart, updated hourly · fetched {formatTime(value.fetchedAt)}
        </Typography>
        <Tooltip title="Reload">
          <span className={classes.reload}>
            <IconButton
              size="small"
              onClick={onReload}
              disabled={reloading}
              aria-label="Reload test results"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box className={classes.matrix}>
        <Box className={classes.mgrid}>
          <span />
          <span className={classes.suiteHead}>E2E</span>
          <span />
          {value.products.map(p => {
            const runs = p[env].runs;
            const state = cellState(runs);
            return (
              <Fragment key={p.product}>
                <span className={classes.rowName}>{p.product}</span>
                <span className={classes.mcell}>
                  {state.kind === 'ok' && (
                    <span className={`${classes.cell} ${classes.cellOk}`}>
                      ✓
                    </span>
                  )}
                  {state.kind === 'failed' && (
                    <span className={`${classes.cell} ${classes.cellBad}`}>
                      {state.label}
                    </span>
                  )}
                  {state.kind === 'none' && (
                    <Tooltip title="No recent runs">
                      <span className={`${classes.cell} ${classes.cellNone}`}>
                        —
                      </span>
                    </Tooltip>
                  )}
                </span>
                <span className={classes.mpad} />
                {state.kind === 'failed' && (
                  <FailureDetail product={p.product} runs={runs} />
                )}
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export interface GroupE2eResultsState {
  value: GroupE2eResults | undefined;
  loading: boolean;
  error: Error | undefined;
  reload: () => void;
}

/**
 * Fetches the group's E2E results once per view, plus a manual reload — the
 * mart only updates hourly, and the group page re-renders at 1 Hz during
 * deploys, so this must never poll.
 *
 * Deliberately a hook on the group content rather than state inside the
 * panel: the panel renders below an early-return loading gate, so owning the
 * fetch there would delay it until the (much slower) PR list request
 * finished. Called up here it goes out with the PR list, not after it.
 */
export function useGroupE2eResults(groupName: string): GroupE2eResultsState {
  const api = useApi(mergeOpsApiRef);
  const [reloadTick, setReloadTick] = useState(0);
  const { value, loading, error } = useAsync(
    () => api.getGroupE2eResults(groupName),
    [groupName, reloadTick],
  );
  const reload = useCallback(() => setReloadTick(t => t + 1), []);
  return { value, loading, error, reload };
}

/**
 * Latest E2E results (kwdp mart) for the group's products, shown above the PR
 * list as a pre-deploy health check.
 *
 * Fail-open by design: a fetch error only shows a message here; deploy
 * actions are never blocked on E2E results.
 */
export const E2eResultsPanel = ({
  results,
}: {
  results: GroupE2eResultsState;
}) => {
  const classes = useStyles();
  const { value, loading, error, reload } = results;

  // Hide entirely for groups without E2E coverage (no e2e-products
  // annotation on any owned component) — and during the initial load, so the
  // panel appears only once it has something to say.
  if (!error && (!value || value.products.length === 0)) return null;

  if (error) {
    return (
      <Box className={classes.panel}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Test results</Typography>
          <Tooltip title="Reload">
            <span className={classes.reload}>
              <IconButton
                size="small"
                onClick={reload}
                disabled={loading}
                aria-label="Reload test results"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography className={classes.error}>
          Couldn't fetch test results ({error.message}). Deploys are not blocked
          by this.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <EnvBlock
        env="dev"
        value={value!}
        onReload={reload}
        reloading={loading}
      />
      <EnvBlock
        env="stg"
        value={value!}
        onReload={reload}
        reloading={loading}
      />
    </>
  );
};
